import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Badge,
  Button,
  Textarea,
  FileUpload,
  Spinner,
  toast,
} from 'prosci-ui';
import {
  guidedUseCaseService,
  type ImportAction,
  type ImportRow,
  type ImportSource,
} from '@/services/guided-use-case-service';

const COLUMN_CONTRACT =
  'canvas_key · slot · definition_of_good · dos · donts · worked_example · completion_criteria';

const ACTION_BADGE: Record<ImportAction, { label: string; variant: 'primary' | 'success' | 'default' | 'destructive' }> = {
  create: { label: 'New', variant: 'primary' },
  update: { label: 'Updated', variant: 'success' },
  unchanged: { label: 'Unchanged', variant: 'default' },
  empty: { label: 'Not filled in', variant: 'default' },
  error: { label: 'Error', variant: 'destructive' },
};

function badgeFor(row: ImportRow) {
  if (row.action !== 'error') return ACTION_BADGE[row.action];

  const first = row.errors[0] ?? '';
  if (first.startsWith('Unknown slot')) return { label: 'Unknown slot', variant: 'destructive' as const };
  if (first.startsWith('Unknown canvas')) return { label: 'Unknown canvas', variant: 'destructive' as const };
  return ACTION_BADGE.error;
}

function splitError(message: string): { reason: string; valid: string | null } {
  const [reason, known] = message.split(' — known ');
  if (!known) return { reason: message, valid: null };

  const [kind, list] = known.split(': ');
  return { reason: `${reason}. This row will be skipped.`, valid: `Valid ${kind}: ${list}` };
}

function previewText(row: ImportRow): string {
  const definition = row.attributes['definition_of_good'];
  return typeof definition === 'string' ? definition : '';
}

interface BulkImportDialogProps {
  canvasKey: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkImportDialog({ canvasKey, open, onOpenChange }: BulkImportDialogProps) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<ImportSource[]>([]);
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(content), 400);
    return () => clearTimeout(timer);
  }, [content]);

  useEffect(() => {
    if (!open) {
      setContent('');
      setFiles([]);
      setDebounced('');
    }
  }, [open]);

  const sources = useMemo<ImportSource[]>(() => {
    const pasted = debounced.trim()
      ? [{ name: 'Pasted rows', content: debounced }]
      : [];
    return [...files, ...pasted];
  }, [files, debounced]);

  const applySources = useMemo<ImportSource[]>(() => {
    const pasted = content.trim() ? [{ name: 'Pasted rows', content }] : [];
    return [...files, ...pasted];
  }, [files, content]);

  const {
    data: preview,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['guidedImportPreview', canvasKey, sources],
    queryFn: () => guidedUseCaseService.importPreview(sources, canvasKey ?? undefined),
    enabled: open && sources.length > 0,
    retry: false,
  });

  const importMutation = useMutation({
    mutationFn: () => guidedUseCaseService.importApply(applySources, canvasKey ?? undefined),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['guidedUseCases'] });
      queryClient.invalidateQueries({ queryKey: ['guidedCanvas'] });
      queryClient.invalidateQueries({ queryKey: ['guidedElement'] });
      toast.success(
        result.skipped > 0
          ? `Imported ${result.written} rows, skipped ${result.skipped}`
          : `Imported ${result.written} rows`
      );
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to import'),
  });

  const rows = preview?.rows ?? [];
  const summary = preview?.summary;

  const summaryLine = useMemo(() => {
    if (!summary) return null;

    const parts: string[] = [];
    if (summary.create > 0) parts.push(`${summary.create} new`);
    if (summary.update > 0) parts.push(`${summary.update} updated`);
    if (summary.unchanged > 0) parts.push(`${summary.unchanged} unchanged`);
    if (summary.empty > 0) parts.push(`${summary.empty} not filled in`);
    if (summary.error > 0) parts.push(`${summary.error} ${summary.error === 1 ? 'error' : 'errors'}`);
    return parts.join(' · ');
  }, [summary]);

  const writable = (summary?.create ?? 0) + (summary?.update ?? 0);
  const errors = summary?.error ?? 0;

  const stale = content !== debounced;

  const confirmLabel = errors > 0
    ? `Import ${writable} ${writable === 1 ? 'row' : 'rows'}, skip ${errors} ${errors === 1 ? 'error' : 'errors'}`
    : `Import ${writable} ${writable === 1 ? 'row' : 'rows'}`;

  const handleFiles = async (chosen: File[]) => {
    if (!chosen.length) return;

    const rejected = chosen.filter((file) => !file.name.toLowerCase().endsWith('.csv'));
    if (rejected.length) {
      toast.error(`Only .csv files can be imported — ${rejected.map((f) => f.name).join(', ')}`);
    }

    const accepted = chosen.filter((file) => file.name.toLowerCase().endsWith('.csv'));
    if (!accepted.length) return;

    const loaded = await Promise.all(
      accepted.map(async (file) => ({ name: file.name, content: await file.text() }))
    );
    setFiles((prev) => [
      ...prev.filter((existing) => !loaded.some((file) => file.name === existing.name)),
      ...loaded,
    ]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:!max-w-[920px]">
        <DialogHeader>
          <DialogTitle>Import definitions</DialogTitle>
          <DialogDescription>
            Rows land against this canvas. A sheet naming a different canvas is refused. Nothing is written until you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-5 overflow-y-auto py-2">
          <div className="rounded-lg border border-dashed bg-muted px-6 py-5 text-center">
            <div className="text-sm font-semibold">Drop your .csv sheets here, or paste rows</div>
            <div className="mt-1.5 text-xs text-muted-foreground">
              Matched on canvas key and slot. Unknown slots are reported, never guessed.
            </div>
            <div className="mt-2.5 font-mono text-[11px] text-muted-foreground">
              {COLUMN_CONTRACT}
            </div>
            <div className="mt-3 flex justify-center">
              <FileUpload
                accept=".csv,text/csv"
                buttonText="Choose files" multiple
                onFilesChange={handleFiles}
                onError={(message: string) => toast.error(message)}
              />
            </div>
          </div>

          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((file) => (
                <span
                  key={file.name}
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs"
                >
                  {file.name}
                  <button
                    type="button"
                    className="text-muted-foreground"
                    onClick={() => setFiles((prev) => prev.filter((f) => f.name !== file.name))}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}

          <Textarea
            className="min-h-[120px] font-mono text-xs"
            placeholder={`canvas_key,slot,definition_of_good,dos,donts,worked_example,completion_criteria`}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />

          {error && (
            <div className="rounded-md bg-destructive-50 px-4 py-3 text-sm text-destructive">
              {(error as Error).message}
            </div>
          )}

          {isFetching && <Spinner />}

          {preview && (
            <>
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold">
                  Preview — {preview.rows.length} {preview.rows.length === 1 ? 'row' : 'rows'} read
                </div>
                <div className="text-xs text-muted-foreground">{summaryLine}</div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[130px]">Sheet</TableHead>
                    <TableHead className="w-[150px]">Canvas</TableHead>
                    <TableHead className="w-[130px]">Slot</TableHead>
                    <TableHead className="w-[120px]">Change</TableHead>
                    <TableHead>Preview</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const badge = badgeFor(row);
                    const isError = row.action === 'error';

                    return (
                      <TableRow
                        key={row.row_number}
                        className={isError ? 'bg-destructive-50/40' : ''}
                      >
                        <TableCell className="align-top text-xs text-muted-foreground">
                          {row.source_name}
                        </TableCell>
                        <TableCell className="align-top">{row.canvas_key}</TableCell>
                        <TableCell
                          className={`align-top font-mono text-xs ${isError ? 'text-destructive' : ''}`}
                        >
                          {row.slot}
                        </TableCell>
                        <TableCell className="align-top">
                          <Badge variant={badge.variant} tint>{badge.label}</Badge>
                        </TableCell>
                        <TableCell className="align-top text-xs text-muted-foreground">
                          {isError ? (
                            row.errors.map((message, index) => {
                              const { reason, valid } = splitError(message);
                              return (
                                <div key={index} className={index > 0 ? 'mt-2' : ''}>
                                  <div className="text-destructive">{reason}</div>
                                  {valid && <div className="mt-1">{valid}</div>}
                                </div>
                              );
                            })
                          ) : (
                            <span className="line-clamp-2">{previewText(row)}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <span className="text-xs text-muted-foreground">
            Imported rows are saved as drafts. You publish each element yourself.
          </span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={writable === 0 || stale || importMutation.isPending}
              onClick={() => importMutation.mutate()}
            >
              {importMutation.isPending ? 'Importing...' : stale ? 'Reading rows…' : confirmLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
